import { useEffect, useRef, useState } from "react";
import Icon from "components/Elements/Icon/Icon.tsx";
import PasswordStrength from "components/Reusable/PasswordStrength/PasswordStrength.tsx";
import generatePassword from "utils/passwordGenerator.ts";
import useAvoidFirstMount from "hooks/useMountedState";
import { useCustomController } from "components/Reusable/Form/useCustomController.js";
import { useContent } from "contexts/contentContext/contentContext.js";

const RegistrationPasswordField = (props: any) => {
  const { fieldControls, hasError, setErrorMessage } =
    useCustomController(props);

  const { getTextByKey } = useContent();

  const [focused, setFocused] = useState(false);

  const [password, setPassword] = useState(fieldControls.value);

  const [type, setType] = useState("password");

  const [passwordStrengthLevel, setPasswordStrengthLevel] = useState("weak");

  const { label, placeholder, showPassword } = props.item;

  const { getValues, setValue } = props;

  const clickedRef = useRef(false);

  // in case this is validation field on generate pass
  // fieldControls.value updating so need to update pass state to keep UI relevant

  useAvoidFirstMount({
    callback: () => {
      if (fieldControls.value !== password) setPassword(fieldControls.value);
    },
    dependencies: [fieldControls.value],
  });

  useEffect(() => {
    fieldControls.onChange(password);
  }, [password]);

  //handle logic that checks if password includes user's email or username
  useEffect(() => {
    const email = (getValues().Email || getValues().Username)?.toLowerCase();
    const emailBeforeAt = email?.split("@")[0];
    if (emailBeforeAt?.length > 3) {
      if (password?.toLowerCase().includes(emailBeforeAt)) {
        setErrorMessage("pass-contains-email-error");
      } else {
        setErrorMessage("");
      }
    }
  }, [password, getValues().Username, getValues().Email]);

  const handleGeneratePassword = () => {
    const newPass = generatePassword();
    setPassword(newPass);
    setType("text");

    // check if validation field exist within this form
    // if it is - autofill generated password to that field as well
    const passValidationFieldExist =
      getValues().hasOwnProperty("PasswordValidation");
    if (passValidationFieldExist) {
      setValue("PasswordValidation", newPass);
    }
  };

  const handleBlur = () => {
    setPassword((prev: string) => prev?.trim());
    setTimeout(() => {
      if (!clickedRef.current) {
        setFocused(false);
      } else {
        clickedRef.current = false;
      }
    }, 100);
  };

  const handleFocus = () => {
    setFocused(true);
    clickedRef.current = false;
  };

  useEffect(() => {
    if (showPassword) {
      setType("text");
    }
  }, [showPassword]);

  const conditionToShowGeneratePassword =
    focused &&
    !props.item.hidePassStrength &&
    passwordStrengthLevel !== "strong";

  return (
    <>
      <label className={`${hasError ? "invalid-input" : ""} `}>
        <div className={`input-label ${props.labelClassName || ""}`}>
          {getTextByKey(label || "signin-password-label")}
        </div>
        <div className="password-input">
          {conditionToShowGeneratePassword && (
            <div
              className="generate-password-element"
              onClick={handleGeneratePassword}
              onMouseDown={() => {
                clickedRef.current = true;
              }}
            >
              <Icon name="key-solid_2" />
              {getTextByKey("generate-password-tooltip-text")}
            </div>
          )}
          <input
            className="input-field square"
            type={type}
            value={password}
            placeholder={getTextByKey(
              placeholder || "signin-password-placeholder"
            )}
            onFocus={handleFocus}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            onBlur={handleBlur}
          />
          <Icon
            blue
            iconClass={`inside-field`}
            name={`show-${type}`}
            onClick={() =>
              setType((prevType) =>
                prevType === "password" ? "text" : "password"
              )
            }
          />
        </div>
      </label>
      {props.item.hidePassStrength ? null : (
        <PasswordStrength
          {...{
            passwordStrengthLevel,
            setPasswordStrengthLevel,
            fieldControls,
            password,
          }}
        />
      )}
    </>
  );
};

export default RegistrationPasswordField;
